import type { ResolutionString } from '$lib/charting_library';
import type { Candle, CandleResult, TempCandle } from '$lib/types/tradingView';
import { wei } from '@synthetixio/wei';
import { DEBT_RATIO_UNIT } from './constants';
import _ from 'lodash';

export const resolutionToSeconds = (resolution: ResolutionString): number => {
	if (!isNaN(Number(resolution))) {
		return Number(resolution) * 60;
	} else {
		const period =
			resolution === '1D'
				? 86400
				: resolution === '3D'
				? 86400 * 3
				: resolution === '7D'
				? 86400 * 7
				: resolution === '30D'
				? 86400 * 30
				: 3600;
		return period;
	}
};

export const getDisplayAsset = (asset: string | null) => {
	return asset ? (asset[0] === 's' ? asset.slice(1) : asset) : null;
};

export const combineDataToPair = (
	baseData: Candle[],
	quoteData: Candle[],
	baseCurrencyIsSUSD: boolean,
	quoteCurrencyIsSUSD: boolean
) => {
	if (baseCurrencyIsSUSD) return quoteData.map(toTempCandle).map(fromTempCandle); // Double map to make sure output is still in the same format
	if (quoteCurrencyIsSUSD) return baseData.map(toTempCandle).map(fromTempCandle);

	if (!(baseData.length && quoteData.length)) return [];

	const baseCandles = baseData.map((candle: Candle) => ({
		isBase: true,
		...toTempCandle(candle)
	}));
	const quoteCandles = quoteData.map(toTempCandle);

	let allCandles: TempCandle[] = [];
	allCandles = allCandles.concat(baseCandles);
	allCandles = allCandles.concat(quoteCandles);
	allCandles = _.orderBy(allCandles, 'timestamp');

	let prevBaseCandle: TempCandle = baseCandles[0];
	let prevQuoteCandle: TempCandle = quoteCandles[0];

	return allCandles.reduce((candles, candle) => {
		let open = wei(0);
		let high = wei(0);
		let low = wei(0);
		let close = wei(0);
		if (candle.isBase) {
			open = candle.open.div(prevQuoteCandle.open);
			high = candle.high.div(prevQuoteCandle.high);
			low = candle.low.div(prevQuoteCandle.low);
			close = candle.close.div(prevQuoteCandle.close);
			prevBaseCandle = candle;
		} else {
			open = prevBaseCandle.open.div(candle.open);
			high = prevBaseCandle.high.div(candle.high);
			low = prevBaseCandle.low.div(candle.low);
			close = prevBaseCandle.close.div(candle.close);
			prevQuoteCandle = candle;
		}
		return candles.concat(
			fromTempCandle({
				timestamp: candle.timestamp,
				open,
				high,
				low,
				close
			} as TempCandle)
		);
	}, [] as Candle[]);
};

const toTempCandle = (n: Candle): TempCandle => {
	return {
		id: n.id,
		synth: n.synth,
		open: wei(n.open),
		high: wei(n.high),
		low: wei(n.low),
		close: wei(n.close),
		timestamp: wei(n.timestamp)
	};
};

const fromTempCandle = (n: TempCandle): Candle => {
	return {
		id: n.id,
		synth: n.synth,
		open: n.open.toNumber(),
		high: n.high.toNumber(),
		low: n.low.toNumber(),
		close: n.close.toNumber(),
		timestamp: n.timestamp.toNumber()
	};
};

export const getRatesEndpoint = (): string => {
	return 'https://api.thegraph.com/subgraphs/name/kwenta/optimism-latest-rates';
};

export const mapCandles = (candles: CandleResult[]): Candle[] => {
	return candles?.map(({ id, synth, open, high, low, close, timestamp }: CandleResult) => {
		return {
			id: id,
			synth: synth,
			open: synth === 'DebtRatio' ? open.div(DEBT_RATIO_UNIT).toNumber() : open.toNumber(),
			high: synth === 'DebtRatio' ? high.div(DEBT_RATIO_UNIT).toNumber() : high.toNumber(),
			low: synth === 'DebtRatio' ? low.div(DEBT_RATIO_UNIT).toNumber() : low.toNumber(),
			close: synth === 'DebtRatio' ? close.div(DEBT_RATIO_UNIT).toNumber() : close.toNumber(),
			timestamp: timestamp.toNumber()
		};
	});
};

export const mapPriceChart = (candles: CandleResult[]): Candle[] => {
	return candles?.map(({ id, synth, open, high, low, close, average, timestamp }: CandleResult) => {
		return {
			id: id,
			synth: synth,
			open: synth === 'DebtRatio' ? open.div(DEBT_RATIO_UNIT).toNumber() : open.toNumber(),
			high: synth === 'DebtRatio' ? high.div(DEBT_RATIO_UNIT).toNumber() : high.toNumber(),
			low: synth === 'DebtRatio' ? low.div(DEBT_RATIO_UNIT).toNumber() : low.toNumber(),
			close: synth === 'DebtRatio' ? close.div(DEBT_RATIO_UNIT).toNumber() : close.toNumber(),
			average: synth === 'DebtRatio' ? average.div(DEBT_RATIO_UNIT).toNumber() : average.toNumber(),
			timestamp: timestamp.toNumber()
		};
	});
};
